import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAISummary } from 'services/assistantSummaries'
import { useNotification } from 'components/Notification'
import { DEFAULT_AI_MODEL } from 'pages/IrwinAssistant/Summaries/utils'

const useAISummaries = willUseDefaultFormatting => {
  const { createNotification } = useNotification()

  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedSecurity, setSelectedSecurity] = useState(null)
  const [selectedEntityId, setSelectedEntityId] = useState(null)
  const [selectedEntityType, setSelectedEntityType] = useState(null)
  const [promptPrefix, setPromptPrefix] = useState(null)
  const [promptSuffix, setPromptSuffix] = useState(null)
  const [selectedDataSource, setSelectedDataSource] = useState('activity')
  const [selectedModel, setSelectedModel] = useState(DEFAULT_AI_MODEL)

  const [isValidToSummarize, setIsValidToSummarize] = useState(false)
  useEffect(() => {
    setIsValidToSummarize(
      Boolean(selectedCustomer && selectedSecurity && selectedEntityId && selectedEntityType),
    )
  }, [selectedCustomer, selectedSecurity, selectedEntityId, selectedEntityType])

  const {
    data: assistantSummaryData,
    isPending: assistantSummaryDataIsLoading,
    error: assistantSummaryError,
  } = useQuery({
    queryKey: [
      'assistantSummary',
      {
        promptPrefix,
        promptSuffix,
        defaultInstructions: willUseDefaultFormatting,
        entityId: selectedEntityId,
        entityType: selectedEntityType,
        customerId: selectedCustomer?.id,
        securityId: selectedSecurity?.id,
        dataSource: selectedDataSource,
        aiModelName: selectedModel,
      },
    ],
    queryFn: getAISummary,
    enabled: Boolean(
      (promptPrefix !== null || promptSuffix !== null) &&
      selectedEntityId &&
      selectedEntityType &&
      selectedCustomer &&
      selectedSecurity?.id &&
      selectedModel,
    ),
    refetchOnWindowFocus: false,
    throwOnError: false,
    gcTime: 0,
  })

  // useQuery no longer accepts onSuccess/onError, so these notifications are raised from the
  // query result instead. createNotification is a fresh function on every NotificationProvider
  // render, and it triggers such a render, so the refs are what stop each effect from looping.
  const notifiedSummary = useRef(null)
  useEffect(() => {
    if (!assistantSummaryData?.status || notifiedSummary.current === assistantSummaryData) {
      return
    }

    notifiedSummary.current = assistantSummaryData
    createNotification({
      message: 'Failed to generate a summary: ' + assistantSummaryData.status,
      variant: 'error',
    })
  }, [assistantSummaryData, createNotification])

  const notifiedError = useRef(null)
  useEffect(() => {
    if (!assistantSummaryError || notifiedError.current === assistantSummaryError) {
      return
    }

    notifiedError.current = assistantSummaryError
    createNotification({
      message: assistantSummaryError.response?.data?.error || assistantSummaryError.message,
      variant: 'error',
    })
  }, [assistantSummaryError, createNotification])

  return {
    selectedCustomer,
    setSelectedCustomer,
    selectedSecurity,
    setSelectedSecurity,
    selectedEntityId,
    setSelectedEntityId,
    selectedEntityType,
    setSelectedEntityType,
    promptPrefix,
    setPromptPrefix,
    promptSuffix,
    setPromptSuffix,
    selectedDataSource,
    setSelectedDataSource,
    selectedModel,
    setSelectedModel,
    assistantSummaryData,
    assistantSummaryDataIsLoading,
    isValidToSummarize,
  }
}

export default useAISummaries
